import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import Modal from '../../../../components/Modal/Modal';
import { updateDunning } from '../../../../services/finance/dunningApi';
import {
   ControlInput,
   ControlSelect,
   FieldHint,
   FieldLabel,
   PrimaryActionButton,
   SecondaryButton,
} from '../../../../styles/dunning/base';
import { financeErrorMessage } from '../../utils/apiError';
import { formatDateBR } from '../../utils/dunningFormat';

// ---------------------------------------------------------------------------
// "+ Novo compromisso" — o ÚNICO caminho que escreve `next_action_at` (D14).
// Salva na hora via PATCH /:id, sem depender do relato da tratativa.
// Sem data = compromisso removido → caso volta para PENDING (fila imediata).
//
// Gravar compromisso é `finance:dunning:interact` (0100) — o gatilho no
// cabeçalho do drawer já gateia por ela.
//
// `canAssign` (= read_all + update): quem não é gestor não reatribui — o campo
// Responsável some e o PATCH NÃO leva a chave assigned_user_id (o backend
// responde 403 assignment_change_forbidden a qualquer toque nela, e o 403
// derruba a tela toda pelo interceptor global). A lista vem do endpoint de
// candidatos (0100 §3.2), não de GET /users.
// ---------------------------------------------------------------------------

export default function CommitModal({
   isOpen,
   caseData = null,
   assignees,
   canAssign,
   onClose,
   onSaved,
}) {
   const [nextDate, setNextDate] = useState('');
   const [assignee, setAssignee] = useState('');
   const [isSaving, setIsSaving] = useState(false);

   // Rascunho re-semeado a cada abertura com o estado atual do caso.
   useEffect(() => {
      if (!isOpen) return;
      setNextDate(caseData?.next_action_at ?? '');
      setAssignee(
         caseData?.assigned_user ? String(caseData.assigned_user.id) : ''
      );
   }, [isOpen, caseData]);

   const handleSave = async () => {
      if (!caseData) return;
      setIsSaving(true);
      try {
         const updated = await updateDunning(caseData.id, {
            nextActionAt: nextDate || null,
            ...(canAssign
               ? { assignedUserId: assignee ? Number(assignee) : null }
               : {}),
         });
         toast.success(
            nextDate
               ? `Compromisso salvo para ${formatDateBR(nextDate)}.`
               : 'Compromisso removido — caso volta para ação imediata.'
         );
         onSaved(updated);
         onClose();
      } catch (err) {
         toast.error(
            financeErrorMessage(err, 'Não foi possível salvar o compromisso.')
         );
      } finally {
         setIsSaving(false);
      }
   };

   return (
      <Modal
         isOpen={isOpen}
         title="Novo compromisso"
         onClose={onClose}
         maxWidth="460px"
         footer={
            <>
               <SecondaryButton type="button" onClick={onClose}>
                  Cancelar
               </SecondaryButton>
               <PrimaryActionButton
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
               >
                  {isSaving ? 'Salvando...' : 'Salvar'}
               </PrimaryActionButton>
            </>
         }
      >
         <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
               <FieldLabel htmlFor="dunning-commit-date">
                  Próxima ação em *
               </FieldLabel>
               <ControlInput
                  id="dunning-commit-date"
                  type="date"
                  value={nextDate}
                  onChange={(event) => setNextDate(event.target.value)}
               />
               <FieldHint>
                  Sem data, o caso volta para a fila de ação imediata.
               </FieldHint>
            </div>
            {canAssign && (
               <div>
                  <FieldLabel htmlFor="dunning-commit-assignee">
                     Responsável
                  </FieldLabel>
                  <ControlSelect
                     id="dunning-commit-assignee"
                     value={assignee}
                     onChange={(event) => setAssignee(event.target.value)}
                  >
                     <option value="">Sem responsável</option>
                     {assignees.map((user) => (
                        <option key={user.id} value={String(user.id)}>
                           {user.nome}
                        </option>
                     ))}
                  </ControlSelect>
               </div>
            )}
            <FieldHint style={{ marginTop: 0 }}>
               Salva na hora, sem depender do relato.
            </FieldHint>
         </div>
      </Modal>
   );
}

CommitModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   // eslint-disable-next-line react/forbid-prop-types
   caseData: PropTypes.object,
   // eslint-disable-next-line react/forbid-prop-types
   assignees: PropTypes.array.isRequired,
   canAssign: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   onSaved: PropTypes.func.isRequired,
};
